/*
 * rg-saved-2026-10-17 — the wishlist page, /pages/wishlist (GH #175, plan §6.4, §6.5).
 * Rendered from the store in assets/rg-saved.js (loaded earlier by layout/theme.liquid; this
 * file does nothing without it). Markup frame: sections/rg-wishlist-page.liquid.
 */
(() => {
  'use strict';

  if (customElements.get('rg-wishlist-page')) return;

  const ACTIVE_KEY = 'rg_saved_active_list';
  const SFL = 'sfl';
  // rg-saved-2026-10-19: wholesale (B2B) — no price baseline, so no "price drops" sort and no `_rg_p`.
  const B2B = !!(window.theme && window.theme.info && window.theme.info.rgSavedB2b);

  const readActive = () => {
    try { return window.localStorage.getItem(ACTIVE_KEY); } catch (e) { return null; }
  };
  const writeActive = (id) => {
    try { window.localStorage.setItem(ACTIVE_KEY, id); } catch (e) { /* convenience only */ }
  };

  const el = (tag, attrs = {}, text) => {
    const node = document.createElement(tag);
    Object.keys(attrs).forEach((k) => {
      if (k === 'className') node.className = attrs[k];
      else if (k === 'dataset') Object.assign(node.dataset, attrs[k]);
      else if (attrs[k] !== false && attrs[k] !== null && attrs[k] !== undefined) node.setAttribute(k, attrs[k] === true ? '' : attrs[k]);
    });
    if (text !== undefined) node.textContent = text;
    return node;
  };

  class RgWishlistPage extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.api = window.rgSaved;
      if (!this.api) return;
      this.ready = true;
      this.store = this.api.store;
      this.t = this.api.t;
      this.tabsEl = this.querySelector('[data-rg-tabs]');
      this.newEl = this.querySelector('[data-rg-new]');
      this.panel = this.querySelector('[data-rg-panel]');
      this.toolbar = this.querySelector('[data-rg-toolbar]');
      this.sortSlot = this.querySelector('[data-rg-sort-slot]');
      this.grid = this.querySelector('[data-rg-grid]');
      this.statusEl = this.querySelector('[data-rg-page-status]');
      this.empty = this.querySelector('[data-rg-empty]');
      this.sort = 'date';
      this.active = readActive();
      this.mode = null; // 'rename' | 'delete' | null
      this.creating = false;
      this.toolbarKey = '';

      this.onStoreChange = () => this.render();
      document.addEventListener('rg:saved:change', this.onStoreChange);
      this.grid.addEventListener('rg:saved:card-ready', () => this.onCardReady());
      this.addEventListener('click', (evt) => this.onClick(evt));
      this.addEventListener('keydown', (evt) => this.onKeydown(evt));
      this.addEventListener('submit', (evt) => this.onSubmit(evt));
      this.addEventListener('change', (evt) => this.onSelectChange(evt));
      this.addEventListener('input', (evt) => this.onInput(evt));
      this.render();
    }

    disconnectedCallback() {
      if (this.onStoreChange) document.removeEventListener('rg:saved:change', this.onStoreChange);
    }

    // ── Data ─────────────────────────────────────────────────────────────────

    buckets() {
      const out = this.store.lists().map((l) => ({ id: l.id, name: l.name, def: l.def, items: l.items }));
      const sfl = this.store.sfl();
      if (sfl.length) out.push({ id: SFL, name: this.t('savedForLater'), def: false, items: sfl });
      return out;
    }

    resolveActive(buckets) {
      if (buckets.some((b) => b.id === this.active)) return this.active;
      const def = buckets.find((b) => b.def);
      return def ? def.id : (buckets[0] ? buckets[0].id : null);
    }

    /** Live card facts (price, availability) from the card cache, for sorting and Add all. */
    cardFacts(item) {
      const html = this.api.cards.peek(item.h, item.vid);
      if (typeof html !== 'string') return null;
      const card = this.api.cards.build(html);
      if (!card) return null;
      const sameVariant = Number(card.dataset.variantId) === item.vid;
      return {
        card,
        price: Number(card.dataset.price),
        available: sameVariant && card.dataset.available === 'true',
        change: this.api.priceChange(card, item)
      };
    }

    sorted(items) {
      const list = items.slice();
      const byDate = (a, b) => Date.parse(b.at) - Date.parse(a.at);
      if (this.sort === 'drops') {
        const drop = (i) => { const f = this.cardFacts(i); return f && f.change && f.change.dir === 'drop' ? f.change.diff / i.p : 0; };
        return list.sort((a, b) => drop(b) - drop(a) || byDate(a, b));
      }
      if (this.sort === 'price') {
        const price = (i) => { const f = this.cardFacts(i); return f && f.price >= 0 ? f.price : Number.MAX_SAFE_INTEGER; };
        return list.sort((a, b) => price(a) - price(b) || byDate(a, b));
      }
      return list.sort(byDate);
    }

    /** Price sorts need every card's live price: load them all (4 at a time), then re-sort. */
    ensureAllLoaded(items) {
      const missing = items.filter((i) => this.api.cards.peek(i.h, i.vid) === undefined);
      if (!missing.length) return;
      Promise.all(missing.map((i) => this.api.cards.load(i.h, i.vid))).then(() => this.render());
    }

    onCardReady() {
      if (this.sort === 'date') return;
      window.clearTimeout(this.resortTimer);
      this.resortTimer = window.setTimeout(() => this.render(), 150);
    }

    // ── Rendering ────────────────────────────────────────────────────────────

    render() {
      const buckets = this.buckets();
      this.active = this.resolveActive(buckets);
      const bucket = buckets.find((b) => b.id === this.active) || null;
      this.renderTabs(buckets);
      this.renderNew(buckets);
      this.renderToolbar(bucket, buckets);
      this.renderSort(bucket);

      const items = bucket ? bucket.items : [];
      this.empty.hidden = items.length > 0;
      this.grid.hidden = items.length === 0;
      if (bucket) {
        this.panel.setAttribute('role', 'tabpanel');
        this.panel.setAttribute('aria-labelledby', `rg-tab-${bucket.id}`);
      } else {
        this.panel.removeAttribute('role');
        this.panel.removeAttribute('aria-labelledby');
      }
      if (this.sort !== 'date') this.ensureAllLoaded(items);
      this.api.renderItems(this.grid, this.sorted(items), this.active || SFL, this.toolbar.querySelector('[data-rg-focus-fallback]'));
    }

    renderTabs(buckets) {
      const focused = document.activeElement && this.tabsEl.contains(document.activeElement)
        ? document.activeElement.dataset.tab : null;
      const frag = document.createDocumentFragment();
      buckets.forEach((b) => {
        const selected = b.id === this.active;
        const tab = el('button', {
          type: 'button',
          role: 'tab',
          id: `rg-tab-${b.id}`,
          className: 'rg-wishlist__tab',
          'aria-selected': selected ? 'true' : 'false',
          'aria-controls': 'rg-wishlist-panel',
          tabindex: selected ? '0' : '-1',
          dataset: { tab: b.id }
        });
        tab.append(el('span', { className: 'rg-wishlist__tab-name' }, b.name), el('span', { className: 'rg-wishlist__tab-count' }, ` (${b.items.length})`));
        frag.appendChild(tab);
      });
      this.tabsEl.replaceChildren(frag);
      this.tabsEl.hidden = buckets.length === 0;
      if (focused) {
        const again = this.tabsEl.querySelector(`[data-tab="${focused}"]`);
        if (again) again.focus();
      }
    }

    renderNew(buckets) {
      const lists = buckets.filter((b) => b.id !== SFL).length;
      if (this.creating && this.newEl.querySelector('form')) return; // keep what is being typed
      this.newEl.replaceChildren();
      if (this.creating) {
        const form = el('form', { className: 'rg-wishlist__new-form flex items-center', dataset: { rgForm: 'new' }, novalidate: true });
        const label = el('label', { className: 'visually-hidden', for: 'rg-new-list-name' }, this.t('newListLabel'));
        const input = el('input', { className: 'input rg-wishlist__input', type: 'text', id: 'rg-new-list-name', maxlength: '40', autocomplete: 'off', 'aria-describedby': 'rg-new-list-error', placeholder: this.t('newListLabel') });
        const create = el('button', { type: 'submit', className: 'btn btn--secondary btn--narrow' }, this.t('create'));
        const cancel = el('button', { type: 'button', className: 'link text-sm', dataset: { rgCmd: 'new-cancel' } }, this.t('cancel'));
        const error = el('p', { className: 'rg-wishlist__error text-sm text-error-text', id: 'rg-new-list-error', role: 'alert', hidden: true });
        form.append(label, input, create, cancel, error);
        this.newEl.appendChild(form);
        input.focus();
        return;
      }
      const btn = el('button', {
        type: 'button',
        className: 'rg-wishlist__tab rg-wishlist__tab--new',
        dataset: { rgCmd: 'new' },
        disabled: lists >= this.store.LIMITS.lists,
        title: lists >= this.store.LIMITS.lists ? this.t('listsFull') : null
      }, this.t('newList'));
      this.newEl.appendChild(btn);
    }

    renderToolbar(bucket, buckets) {
      // No `this.sort` in the key: rebuilding on a sort change destroyed the focused control
      // (a11y F4). The sort control itself is no longer in the toolbar (renderSort).
      const key = bucket
        ? [bucket.id, bucket.name, bucket.def, bucket.items.length > 0, this.mode, buckets.length].join('|')
        : 'none';
      if (key === this.toolbarKey) return; // unchanged: never rebuild under the shopper's cursor
      this.toolbarKey = key;
      // A rebuild for another reason keeps focus on the same control (Add all, Rename, ...).
      const active = document.activeElement;
      let keep = null;
      if (active && this.toolbar.contains(active)) {
        if (active.id) keep = `#${active.id}`;
        else if (active.dataset.rgCmd) keep = `[data-rg-cmd="${active.dataset.rgCmd}"]`;
      }
      this.toolbar.replaceChildren();
      if (!bucket) return;
      const isList = bucket.id !== SFL;

      // One row (Jason 2026-10-17): the list name on the left, its actions as small text links on
      // the right; it wraps under the name on a phone. Rename swaps the row for its form.
      const head = el('div', { className: 'rg-wishlist__head flex flex-wrap items-center' });
      if (isList && this.mode === 'rename') {
        const form = el('form', { className: 'rg-wishlist__rename flex items-center', dataset: { rgForm: 'rename' }, novalidate: true });
        const label = el('label', { className: 'visually-hidden', for: 'rg-rename-input' }, this.t('renameLabel'));
        const input = el('input', { className: 'input rg-wishlist__input', type: 'text', id: 'rg-rename-input', maxlength: '40', autocomplete: 'off', value: bucket.name, 'aria-describedby': 'rg-rename-error' });
        const save = el('button', { type: 'submit', className: 'btn btn--secondary btn--narrow' }, this.t('pageSave'));
        const cancel = el('button', { type: 'button', className: 'link text-sm', dataset: { rgCmd: 'rename-cancel' } }, this.t('cancel'));
        const error = el('p', { className: 'rg-wishlist__error text-sm text-error-text', id: 'rg-rename-error', role: 'alert', hidden: true });
        form.append(label, input, save, cancel, error);
        head.appendChild(form);
      } else {
        const name = el('div', { className: 'rg-wishlist__name flex flex-wrap items-center' });
        name.appendChild(el('h2', { className: 'h4 rg-wishlist__list-name mb-0', tabindex: '-1', dataset: { rgFocusFallback: '' } }, bucket.name));
        head.appendChild(name);
      }

      if (isList && this.mode !== 'rename') {
        const actions = el('div', { className: `rg-wishlist__list-actions flex flex-wrap items-center text-sm${this.mode === 'delete' ? ' rg-wishlist__list-actions--confirm' : ''}` });
        if (this.mode === 'delete') {
          const others = buckets.filter((b) => b.id !== SFL && b.id !== bucket.id);
          const target = others.find((b) => b.def) || others[0];
          actions.appendChild(el('p', { className: 'rg-wishlist__confirm-text m-0', id: 'rg-delete-confirm' }, this.t('deleteConfirm', { list: bucket.name })));
          if (target && bucket.items.length) {
            actions.appendChild(el('button', { type: 'button', className: 'btn btn--secondary btn--narrow', dataset: { rgCmd: 'delete-move', target: target.id } }, this.t('deleteMove', { list: target.name })));
          }
          actions.appendChild(el('button', { type: 'button', className: 'btn btn--primary btn--narrow', dataset: { rgCmd: 'delete-confirm' }, 'aria-describedby': 'rg-delete-confirm' }, this.t('deleteConfirmButton')));
          actions.appendChild(el('button', { type: 'button', className: 'link', dataset: { rgCmd: 'delete-cancel' } }, this.t('cancel')));
        } else {
          const links = [el('button', { type: 'button', className: 'link', dataset: { rgCmd: 'rename' } }, this.t('rename'))];
          if (!bucket.def) links.push(el('button', { type: 'button', className: 'link', dataset: { rgCmd: 'default' } }, this.t('makeDefault')));
          links.push(el('button', { type: 'button', className: 'link', dataset: { rgCmd: 'delete' } }, this.t('deleteList')));
          links.forEach((link, i) => {
            if (i) actions.appendChild(el('span', { className: 'rg-wishlist__sep', 'aria-hidden': 'true' }, '·'));
            actions.appendChild(link);
          });
        }
        head.appendChild(actions);
      }
      // Jason 2026-10-17: order is "My Wishlist · Rename list · Delete list · (Default list)" — the
      // pill follows the links so they read as actions on the name beside them.
      if (isList && bucket.def && this.mode !== 'rename' && this.mode !== 'delete') {
        head.appendChild(el('span', { className: 'rg-wishlist__badge text-sm' }, this.t('defaultList')));
      }
      this.toolbar.appendChild(head);

      // Sort lives beside the page heading now (renderSort); this row holds Add all only.
      if (bucket.items.length && isList) {
        const controls = el('div', { className: 'rg-wishlist__controls flex flex-wrap items-center' });
        const addAll = el('button', { type: 'button', className: 'btn btn--primary', dataset: { rgCmd: 'add-all' } }, this.t('addAll'));
        if (this.adding) {
          addAll.setAttribute('aria-disabled', 'true');
          addAll.setAttribute('aria-busy', 'true');
          addAll.classList.add('is-loading');
        }
        controls.appendChild(addAll);
        this.toolbar.appendChild(controls);
      }

      if (this.mode === 'rename') {
        const input = this.toolbar.querySelector('#rg-rename-input');
        if (input) { input.focus(); input.select(); }
      } else if (this.mode === 'delete') {
        const first = this.toolbar.querySelector('[data-rg-cmd="delete-move"], [data-rg-cmd="delete-confirm"]');
        if (first) first.focus();
      } else if (keep) {
        const again = this.toolbar.querySelector(keep);
        if (again) again.focus();
      }
    }

    /**
     * "Sort by" beside the page heading (Jason 2026-10-17), built ONCE with the theme's own
     * <custom-select> (the control the collection page's Sort by uses) instead of a native
     * <select>, whose OS menu opened far from the control. Shown while the open list has items.
     * Keyboard: Space / Arrow keys / Enter open it; arrows move, Enter or Space chooses, Escape
     * cancels, typing jumps; focus returns to the button either way (custom-select.js). This page
     * adds what the theme component lacks for screen readers: the button's name carries the
     * current value ("Sort by, Date saved"), and aria-activedescendant follows the highlighted
     * option while the list is open.
     */
    renderSort(bucket) {
      if (!this.sortSlot) return;
      if (!this.sortSlot.firstChild) this.buildSort();
      this.sortSlot.hidden = !(bucket && bucket.items.length);
    }

    buildSort() {
      const id = 'rg-wishlist-sort';
      const options = B2B
        ? [['date', 'sortDate'], ['price', 'sortPrice']]
        : [['date', 'sortDate'], ['drops', 'sortDrops'], ['price', 'sortPrice']];
      const current = Math.max(0, options.findIndex(([value]) => value === this.sort));
      // Built inside a <template> so <custom-select> upgrades only once its children exist (its
      // constructor reads them); document.createElement would run it on an empty element.
      const tpl = document.createElement('template');
      tpl.innerHTML = `<custom-select id="${id}" data-rg-sort></custom-select>`;
      const select = tpl.content.firstElementChild;
      const label = el('label', { className: 'label rg-wishlist__sort-label', id: `${id}-label`, for: `${id}-button` }, this.t('sortLabel'));
      const box = el('div', { className: 'custom-select relative' });
      const button = el('button', {
        className: 'custom-select__btn input items-center',
        type: 'button',
        id: `${id}-button`,
        'aria-expanded': 'false',
        'aria-haspopup': 'listbox',
        'aria-labelledby': `${id}-label ${id}-value`
      });
      button.appendChild(el('span', { className: 'text-start', id: `${id}-value` }, this.t(options[current][1])));
      button.insertAdjacentHTML('beforeend', '<svg width="20" height="20" viewBox="0 0 24 24" class="icon" role="presentation" focusable="false" aria-hidden="true"><path d="M20 8.5 12.5 16 5 8.5" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>');
      const listbox = el('ul', {
        className: 'custom-select__listbox absolute invisible',
        role: 'listbox',
        tabindex: '-1',
        'aria-hidden': 'true',
        'aria-labelledby': `${id}-label`,
        'aria-activedescendant': `${id}-opt-${current}`,
        hidden: true
      });
      options.forEach(([value, key], i) => {
        const option = el('li', {
          className: 'custom-select__option flex items-center js-option',
          id: `${id}-opt-${i}`,
          role: 'option',
          'aria-selected': i === current ? 'true' : 'false',
          dataset: { value }
        });
        option.appendChild(el('span', { className: 'pointer-events-none' }, this.t(key)));
        listbox.appendChild(option);
      });
      box.append(button, listbox);
      select.append(label, box);
      this.sortSlot.appendChild(select); // connected: <custom-select> upgrades here

      const syncActive = () => {
        // Runs after custom-select's own handlers (registered first, at upgrade).
        const option = select.listboxOpen ? select.focusedOption : select.selectedOption;
        if (option && option.id) listbox.setAttribute('aria-activedescendant', option.id);
      };
      select.addEventListener('keydown', syncActive);
      listbox.addEventListener('mouseover', syncActive);
      // Enter on the closed button: custom-select opens on mousedown / Space / arrows only.
      button.addEventListener('click', (evt) => {
        if (evt.detail !== 0 || select.listboxOpen || typeof select.showListbox !== 'function') return;
        select.showListbox();
      });
    }

    setStatus(message) {
      this.statusEl.textContent = message || '';
    }

    /** Inline form error; aria-invalid on the input while it shows (a11y F14). */
    showFormError(form, message, focus = true) {
      const error = form.querySelector('[role="alert"]');
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
      const input = form.querySelector('input');
      if (!input) return;
      if (message) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
      if (focus) input.focus();
    }

    onInput(evt) {
      const input = evt.target;
      if (!input.matches || !input.matches('input[aria-invalid="true"]')) return;
      const form = input.closest('form[data-rg-form]');
      if (form) this.showFormError(form, '', false); // typing clears the error
    }

    // ── Events ───────────────────────────────────────────────────────────────

    activate(id, focus) {
      if (!id || id === this.active) return;
      this.active = id;
      this.mode = null;
      writeActive(id);
      this.render();
      if (focus) {
        const tab = this.tabsEl.querySelector(`[data-tab="${id}"]`);
        if (tab) tab.focus();
      }
    }

    focusAfter(selector) {
      window.requestAnimationFrame(() => {
        const target = this.querySelector(selector);
        if (target) target.focus();
      });
    }

    onClick(evt) {
      const tab = evt.target.closest('[role="tab"][data-tab]');
      if (tab) {
        this.activate(tab.dataset.tab, true);
        return;
      }
      const cmdBtn = evt.target.closest('[data-rg-cmd]');
      if (!cmdBtn) return;
      const cmd = cmdBtn.dataset.rgCmd;
      const id = this.active;
      switch (cmd) {
        case 'new':
          this.creating = true;
          this.renderNew(this.buckets());
          break;
        case 'new-cancel':
          this.creating = false;
          this.renderNew(this.buckets());
          this.focusAfter('[data-rg-cmd="new"]');
          break;
        case 'rename':
          this.mode = 'rename';
          this.render();
          break;
        case 'rename-cancel':
          this.mode = null;
          this.render();
          this.focusAfter('[data-rg-cmd="rename"]');
          break;
        case 'default': {
          const res = this.store.setDefault(id);
          if (res.ok && !res.noop) this.setStatus(this.t('announceDefault', { list: res.list }));
          // "Make default" is gone from the rebuilt toolbar: land on the next action, Rename.
          this.focusAfter('[data-rg-cmd="rename"]');
          break;
        }
        case 'delete':
          this.mode = 'delete';
          this.render();
          break;
        case 'delete-cancel':
          this.mode = null;
          this.render();
          this.focusAfter('[data-rg-cmd="delete"]');
          break;
        case 'delete-move':
        case 'delete-confirm': {
          const res = this.store.deleteList(id, cmd === 'delete-move' ? cmdBtn.dataset.target : null);
          if (!res.ok) {
            this.setStatus(this.api.limitMessage(res.error));
            return;
          }
          this.mode = null;
          this.active = null;
          this.render();
          this.setStatus(this.t('announceDeleted', { list: res.list }));
          this.focusAfter('[role="tab"][aria-selected="true"], [data-rg-cmd="new"]');
          break;
        }
        case 'add-all':
          this.addAll(cmdBtn);
          break;
        default:
      }
    }

    onKeydown(evt) {
      const tab = evt.target.closest('[role="tab"][data-tab]');
      if (tab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(evt.key)) {
        const tabs = Array.from(this.tabsEl.querySelectorAll('[role="tab"]'));
        const i = tabs.indexOf(tab);
        let next = i;
        if (evt.key === 'ArrowRight') next = (i + 1) % tabs.length;
        if (evt.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
        if (evt.key === 'Home') next = 0;
        if (evt.key === 'End') next = tabs.length - 1;
        evt.preventDefault();
        this.activate(tabs[next].dataset.tab, true);
        return;
      }
      if (evt.key === 'Escape') {
        const form = evt.target.closest('form[data-rg-form]');
        if (!form) return;
        evt.preventDefault();
        if (form.dataset.rgForm === 'new') {
          this.creating = false;
          this.renderNew(this.buckets());
          this.focusAfter('[data-rg-cmd="new"]');
        } else {
          this.mode = null;
          this.render();
          this.focusAfter('[data-rg-cmd="rename"]');
        }
      }
    }

    onSubmit(evt) {
      const form = evt.target.closest('form[data-rg-form]');
      if (!form) return;
      evt.preventDefault();
      const value = (form.querySelector('input') || {}).value || '';
      if (form.dataset.rgForm === 'new') {
        const res = this.store.createList(value);
        if (!res.ok) {
          this.showFormError(form, this.api.limitMessage(res.error));
          return;
        }
        this.creating = false;
        this.active = res.listId;
        writeActive(res.listId);
        this.mode = null;
        this.render();
        this.focusAfter(`[data-tab="${res.listId}"]`);
      } else if (form.dataset.rgForm === 'rename') {
        const res = this.store.renameList(this.active, value);
        if (!res.ok) {
          this.showFormError(form, this.api.limitMessage(res.error));
          return;
        }
        this.mode = null;
        this.render();
        if (!res.noop) this.setStatus(this.t('announceRenamed', { list: res.list }));
        this.focusAfter('[data-rg-cmd="rename"]');
      }
    }

    /** <custom-select> announces a choice as a bubbling `change` with detail.selectedValue. */
    onSelectChange(evt) {
      if (!evt.target.matches || !evt.target.matches('[data-rg-sort]')) return;
      const value = evt.detail && evt.detail.selectedValue;
      if (!value || value === this.sort) return;
      this.sort = value;
      this.render();
    }

    /** Sequential adds (repeat-add protocol each time), unavailable items skipped; list kept. */
    async addAll(btn) {
      const bucketId = this.active;
      const items = this.store.items(bucketId);
      if (!items.length || this.adding) return;
      // Busy WITHOUT `disabled`: disabling the focused button dropped focus to <body> (a11y F5).
      this.adding = true;
      const setBusy = (busy) => {
        const live = this.toolbar.querySelector('[data-rg-cmd="add-all"]'); // may have been rebuilt
        [btn, live].forEach((b) => {
          if (!b) return;
          if (busy) {
            b.setAttribute('aria-disabled', 'true');
            b.setAttribute('aria-busy', 'true');
            b.classList.add('is-loading');
          } else {
            b.removeAttribute('aria-disabled');
            b.removeAttribute('aria-busy');
            b.classList.remove('is-loading');
          }
        });
      };
      setBusy(true);
      this.setStatus(this.t('adding'));
      await Promise.all(items.map((i) => this.api.cards.load(i.h, i.vid)));
      let added = 0;
      let skipped = 0;
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        const facts = this.cardFacts(item);
        if (!facts || !facts.available) {
          skipped += 1;
          continue;
        }
        const line = { id: item.vid, quantity: 1 };
        if (!B2B && facts.price >= 0 && facts.card.dataset.currency) line.properties = { _rg_p: `${facts.card.dataset.currency}:${facts.price}` };
        // eslint-disable-next-line no-await-in-loop
        const res = await this.api.cart.add([line], null);
        if (res.ok) added += 1;
        else skipped += 1;
      }
      this.adding = false;
      setBusy(false);
      let message = this.t('addedAll', { added, total: items.length });
      if (skipped) message += ` ${this.t('skipped', { count: skipped })}`;
      if (!added || !document.querySelector('cart-drawer')) {
        // No drawer opens (nothing added, or the cart is a page): the page's own status says it.
        this.setStatus(message);
        if (added) this.api.refreshCart({ open: true, opener: btn });
        return;
      }
      // The cart drawer opens as a modal, and the page status is outside it (inert to screen
      // readers): announce the summary INSIDE the drawer once it is open (rgSaved.announce
      // targets the open dialog), and put it on the page when the drawer closes.
      this.setStatus('');
      let said = false;
      const say = () => {
        if (said) return;
        said = true;
        document.removeEventListener('on:cart-drawer:after-open', say);
        this.api.announce(message);
      };
      const onClose = () => {
        document.removeEventListener('on:cart-drawer:after-close', onClose);
        this.setStatus(message);
      };
      document.addEventListener('on:cart-drawer:after-open', say);
      document.addEventListener('on:cart-drawer:after-close', onClose);
      window.setTimeout(() => {
        if (said) return;
        if (document.querySelector('cart-drawer[open]')) {
          say();
          return;
        }
        // The drawer never opened: fall back to the page's own status.
        said = true;
        document.removeEventListener('on:cart-drawer:after-open', say);
        document.removeEventListener('on:cart-drawer:after-close', onClose);
        this.setStatus(message);
      }, 3000);
      this.api.refreshCart({ open: true, opener: btn });
    }
  }

  customElements.define('rg-wishlist-page', RgWishlistPage);
})();
